/**
 * Create async bulk download job (submit attachment IDs)
 * @param {Array<number>} attachmentIds - Array of attachment IDs to download
 * @param {string} uploadFilename - Optional filename for the ZIP (defaults to 'bulk_download')
 * @returns {Promise<{job_id: string, status: string, total_attachments: number, message: string}>}
 */
const createBulkDownloadJob = (attachmentIds, uploadFilename = 'bulk_download') => {
  const url = `${apiUrl}/bulk-attachments/download`;
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `JWT ${authService.getToken()}`,
    },
    body: JSON.stringify({ 
      attachment_ids: attachmentIds,
      upload_filename: uploadFilename,
    }),
  };
  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || 'Failed to submit download job');
        });
      }
      return response.json();
    });
};

/**
 * List all async bulk download jobs for current user
 * Returns jobs with presigned download URLs (valid for 1 hour)
 * Note: Downloaded jobs are excluded (one-time downloadable)
 * @returns {Promise<Array<{id: number, upload_filename: string, zip_filename: string, download_url: string, status: string, created_at: string}>>}
 */
const listBulkDownloadJobs = () => {
  const url = `${apiUrl}/bulk-attachments/bulk-download/list`;
  const options = {
    method: 'GET',
    headers: {
      Authorization: `JWT ${authService.getToken()}`,
    },
  };
  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || 'Failed to fetch download jobs');
        });
      }
      return response.json();
    })
    .then((response) => response.data || []);  // Changed from 'zips' to 'data'
};

/**
 * Mark a bulk download job as downloaded (and trigger S3 cleanup)
 * This deletes the ZIP file from S3 after user downloads it
 * @param {number} jobId - The download job ID
 * @returns {Promise<{status: string}>}
 */
const markBulkDownloadJobDownloaded = (jobId) => {
  const url = `${apiUrl}/bulk-attachments/bulk-download/mark-downloaded/${jobId}`;
  const options = {
    method: 'POST',
    headers: {
      Authorization: `JWT ${authService.getToken()}`,
    },
  };
  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || 'Failed to mark as downloaded');
        });
      }
      return response.json();
    });
};
/**
 * Attachment Lookup Service
 * Handles file upload with voucher/deal IDs and attachment downloads
 */

/* global CONFIG */
import authService from './auth-service';
import superFetch from '../utils/super-fetch';

const { apiUrl } = CONFIG;

/**
 * Upload file containing voucher/deal IDs and retrieve attachment data
 * @param {File} file - CSV or XLSX file with voucher_id and/or deal_id columns
 * @returns {Promise} Response with attachment data
 */
const uploadAttachmentLookupFile = (file) => {
  const url = `${apiUrl}/bulk-attachments/upload`;
  
  const formData = new FormData();
  formData.append('file', file);
  
  const options = {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `JWT ${authService.getToken()}`,
    },
  };
  
  // Don't set Content-Type header - browser will set it with boundary
  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || 'Upload failed');
        });
      }
      return response.json();
    });
};

/**
 * Download a single attachment by ID (uses dedicated attachment lookup endpoint)
 * @param {number} attachmentId - Attachment ID
 * @returns {Promise} Response with download URL
 */
const downloadSingleAttachment = (attachmentId) => {
  const url = `${apiUrl}/attachment-lookup/download/${attachmentId}`;
  const options = {
    method: 'GET',
  };
  
  return superFetch(url, options)
    .then(response => response.json())
    .then((res) => {
      if (res.attachment_download_url) {
        // Open download in new window
        window.open(res.attachment_download_url, '_blank');
        return res;
      }
      throw new Error(res.error || 'No download URL received');
    });
};

/**
 * Bulk download multiple attachments as ZIP
 * 
 * @deprecated This synchronous download method is deprecated. 
 * Use createBulkDownloadJob() for async bulk downloads with better handling of large files.
 * 
 * The async approach:
 * 1. Call createBulkDownloadJob() to submit job
 * 2. Poll listBulkDownloadJobs() to get status and download URL
 * 3. Download from S3 using the presigned URL
 * 4. Call markBulkDownloadJobDownloaded() to cleanup
 * 
 * @param {Array<number>} attachmentIds - Array of attachment IDs
 * @returns {Promise} Response with ZIP file download
 */
const downloadBulkAttachments = (attachmentIds) => {
  const url = `${apiUrl}/bulk-attachments/download`;
  
  const options = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `JWT ${authService.getToken()}`,
    },
    body: JSON.stringify({ attachment_ids: attachmentIds }),
  };
  
  // Fetch as blob to trigger file download
  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || 'Download failed');
        });
      }
      
      return response.blob();
    })
    .then((blob) => {
      // Create download link and trigger download - same pattern as single download
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `bulk_attachments_${new Date().getTime()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { success: true };
    });
};

/**
 * Export attachment lookup data to Excel
 * Sends the original uploaded file to backend which re-reads and exports
 * @param {File} file - Original uploaded CSV/Excel file
 * @returns {Promise} Response with Excel file download
 */
const exportDataToExcel = (file) => {
  const url = `${apiUrl}/attachment-lookup/export`;
  
  const formData = new FormData();
  formData.append('file', file);
  
  const options = {
    method: 'POST',
    body: formData,
    headers: {
      Authorization: `JWT ${authService.getToken()}`,
    },
  };
  
  // Fetch as blob to trigger file download
  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(error.error || 'Export failed');
        });
      }
      
      return response.blob();
    })
    .then((blob) => {
      // Create download link and trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `attachment_lookup_export_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { success: true };
    });
};

export {
  uploadAttachmentLookupFile,
  downloadSingleAttachment,
  downloadBulkAttachments,
  exportDataToExcel,
  createBulkDownloadJob,
  listBulkDownloadJobs,
  markBulkDownloadJobDownloaded,
};

export default {
  uploadAttachmentLookupFile,
  downloadSingleAttachment,
  downloadBulkAttachments,
  exportDataToExcel,
  createBulkDownloadJob,
  listBulkDownloadJobs,
  markBulkDownloadJobDownloaded,
};
